import { ChevronRight } from "lucide-react";
import type { IScopeRelatedItem } from "@/api";
import { cn } from "@/lib/utils";

const STATUS_CONFIG: Record<
    string,
    { label: string; textClass: string; dotClass: string; pulse: boolean }
> = {
    pending: {
        label: "Waiting for Document",
        textClass: "text-blue-600",
        dotClass: "bg-blue-600",
        pulse: true,
    },
    processing: {
        label: "In Progress",
        textClass: "text-amber-600",
        dotClass: "bg-amber-600",
        pulse: true,
    },
    complete: {
        label: "Complete",
        textClass: "text-green-600",
        dotClass: "bg-green-600",
        pulse: false,
    },
    failed: {
        label: "Failed",
        textClass: "text-red-600",
        dotClass: "bg-red-600",
        pulse: false,
    },
};

export const StatusDot = ({
    status,
}: {
    status: IScopeRelatedItem["status"];
}) => {
    if (!status) return null;
    const config = STATUS_CONFIG[status];
    if (!config) return null;

    return (
        <span
            className={cn(
                "inline-flex flex-shrink-0 items-center gap-1",
                config.textClass
            )}
        >
            <span
                className={cn(
                    "size-1.5 flex-shrink-0 rounded-full",
                    config.dotClass,
                    config.pulse && "animate-pulse"
                )}
            />
            <span className="text-[0.65rem] leading-none font-medium whitespace-nowrap">
                {config.label}
            </span>
        </span>
    );
};

const METADATA_MAX_CHARS = 18;

const MetadataItem = ({ text }: { text: string }) => {
    const isTruncated = text.length > METADATA_MAX_CHARS;
    if (!isTruncated) return <>{text}</>;
    const displayText = `${text.slice(0, METADATA_MAX_CHARS).trim()}...`;
    return <span title={text}>{displayText}</span>;
};

interface RelatedItemCardProps {
    icon: React.ElementType;
    iconColorClass: string;
    title: string;
    metadata: (string | null)[];
    statusIndicator?: React.ReactNode;
    onClick: () => void;
}

export const RelatedItemCard = ({
    icon: Icon,
    iconColorClass,
    title,
    metadata,
    statusIndicator,
    onClick,
}: RelatedItemCardProps) => {
    const filtered = metadata.filter(Boolean) as string[];

    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "border-border bg-background group flex w-full items-center gap-3 rounded-[10px] border p-2.5 text-left transition-colors outline-none",
                "hover:bg-foreground/[0.015] hover:border-border/60",
                "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-3"
            )}
        >
            <span
                className={cn(
                    "flex size-8 flex-shrink-0 items-center justify-center rounded-md",
                    iconColorClass
                )}
            >
                <Icon size={15} strokeWidth={2.2} />
            </span>

            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-2">
                    <span
                        title={title}
                        className="min-w-0 flex-1 truncate text-[0.8rem] leading-tight font-medium"
                    >
                        {title}
                    </span>
                    {statusIndicator}
                </span>
                <span className="text-muted-foreground/80 block truncate text-[0.7rem] leading-tight">
                    {filtered.map((item, i) => (
                        <span key={i}>
                            {i > 0 && " · "}
                            <MetadataItem text={item} />
                        </span>
                    ))}
                </span>
            </span>

            <ChevronRight
                className="text-muted-foreground/50 group-hover:text-muted-foreground size-4 flex-shrink-0 transition-colors"
                strokeWidth={2}
            />
        </button>
    );
};
